import { useCallback, useEffect, useState } from 'react'
import { pruefePasswort } from '@raporto/shared'
import { Passwortfeld } from '../ui/Passwortfeld.js'
import type { SessionUser } from '@raporto/shared'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { api } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { uuidv7 } from '../net/ids.js'

/**
 * Sicherheit des eigenen Kontos.
 *
 * Raporto führt Kundenadressen, Rechnungen, eine IBAN und Zahlungen. Ein
 * Passwort allein schützt das nicht – es steht bei den meisten Leuten in einem
 * Browser gespeichert und bei vielen zusätzlich in drei anderen Diensten
 * gleichlautend.
 *
 * Der Bildschirm führt durch vier Schritte und hält sich an eine Regel, die
 * für den Benutzer unsichtbar, aber entscheidend ist: der Schutz ist erst
 * aktiv, wenn ein Code aus der App wirklich funktioniert hat. Wer die Seite
 * mittendrin schliesst, hat nichts halb Eingerichtetes – nur nichts.
 */

type Schritt = 'uebersicht' | 'qr' | 'codes' | 'abschalten'

export function Security({
  session,
  aufSession,
  ohneKopf
}: {
  session: SessionUser
  /** Meldet die frische Sitzung nach oben – siehe bestaetigen(). */
  aufSession?: (s: SessionUser) => void
  /** Eingebettet in die Einstellungen: dort trägt die Seite schon einen Kopf. */
  ohneKopf?: boolean
}): JSX.Element {
  const [status, setStatus] = useState<{
    twoFactorEnabled: boolean
    twoFactorRequired: boolean
    sensitiveAllowed: boolean
    setupLaeuft: boolean
    wiederherstellungscodesOffen: number
  } | null>(null)
  const [schritt, setSchritt] = useState<Schritt>('uebersicht')
  const [busy, setBusy] = useState<string | null>(null)

  const [qrSvg, setQrSvg] = useState('')
  const [schluessel, setSchluessel] = useState('')
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [bestaetigt, setBestaetigt] = useState(false)

  const [passwort, setPasswort] = useState('')
  const [abschaltCode, setAbschaltCode] = useState('')

  /* Passwort ändern – eigener Zustand, damit nichts mit dem Abschalten kollidiert. */
  const [pwAlt, setPwAlt] = useState('')
  const [pwNeu, setPwNeu] = useState('')
  const [pwNeu2, setPwNeu2] = useState('')
  const [pwFehler, setPwFehler] = useState<string | null>(null)
  const [pwFertig, setPwFertig] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setStatus(await api.securityStatus())
    } catch (err) {
      notifyError(err)
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  async function mit<T>(name: string, fn: () => Promise<T>): Promise<T | null> {
    setBusy(name)
    try {
      return await fn()
    } catch (err) {
      notifyError(err)
      return null
    } finally {
      setBusy(null)
    }
  }

  async function einrichtenStarten(): Promise<void> {
    const antwort = await api.zweiFaktorSetup(uuidv7())
    setQrSvg(antwort.qrSvg)
    setSchluessel(antwort.manuellerSchluessel)
    setCode('')
    setSchritt('qr')
  }

  async function bestaetigen(): Promise<void> {
    const antwort = await api.zweiFaktorBestaetigen(code.trim(), uuidv7())
    setCodes(antwort.recoveryCodes)
    setBestaetigt(false)
    setSchritt('codes')
    /*
      Der QR-Code verschwindet in derselben Bewegung.

      Er enthält das Geheimnis. Auf einem Bildschirm, der offen stehenbleibt,
      während jemand Kaffee holt, ist das genau die Art Nachlässigkeit, gegen
      die dieser ganze Bildschirm gebaut ist.
    */
    setQrSvg('')
    setSchluessel('')
    await laden()

    /*
      Die Sitzung neu holen – sonst bleiben Zahlungen, Export und Firmendaten
      gesperrt, bis jemand die Seite neu lädt.

      Der Server hat die laufende Sitzung beim Bestätigen bereits als geprüft
      vermerkt (wer eben einen Code getippt hat, hat den zweiten Faktor
      erbracht). Nur der Browser wusste es noch nicht, und ein Bildschirm voller
      grauer Knöpfe direkt nach dem Einrichten ist die zuverlässigste Art,
      jemanden dazu zu bringen, den Schutz gleich wieder abzuschalten.
    */
    const me = await api.me()
    if (me.session) aufSession?.(me.session as SessionUser)
  }

  async function abschalten(): Promise<void> {
    await api.zweiFaktorAbschalten(passwort, abschaltCode.trim(), uuidv7())
    setPasswort('')
    setAbschaltCode('')
    setSchritt('uebersicht')
    notify.ok('Zwei-Faktor-Authentifizierung abgeschaltet.')
    await laden()
    const me = await api.me()
    if (me.session) aufSession?.(me.session as SessionUser)
  }

  if (!status) {
    return <>{!ohneKopf && <OfficeHead titel="Sicherheit" unter="Wird geladen …" />}</>
  }

  return (
    <>
      {!ohneKopf && <OfficeHead titel="Sicherheit" unter={`Konto ${session.email}`} />}

      {/* ---------- Passwort ---------- */}

      <div className="o-card" id="passwort-block">
        <div className="o-card-head">
          <h3>Passwort ändern</h3>
          <span className="o-soft">{session.email}</span>
        </div>
        <div className="o-pad">
          {pwFehler && (
            <div className="o-band alarm" id="pw-fehler">
              <span>{pwFehler}</span>
            </div>
          )}
          {pwFertig && (
            <div className="o-band erfolg" id="pw-fertig">
              <span>{pwFertig}</span>
            </div>
          )}

          <div className="o-grid o-grid-3">
            <div className="o-field">
              <label htmlFor="pw-alt">Bisheriges Passwort</label>
              <input
                id="pw-alt"
                type="password"
                autoComplete="current-password"
                value={pwAlt}
                onChange={(e) => setPwAlt(e.target.value)}
              />
            </div>
            <Passwortfeld
              id="pw-neu"
              stil="buero"
              label="Neues Passwort"
              wert={pwNeu}
              aufWert={setPwNeu}
            />
            <div className="o-field">
              <label htmlFor="pw-neu2">Neues Passwort wiederholen</label>
              <input
                id="pw-neu2"
                type="password"
                autoComplete="new-password"
                value={pwNeu2}
                onChange={(e) => setPwNeu2(e.target.value)}
              />
              {pwNeu2.length > 0 && pwNeu !== pwNeu2 && (
                <span className="hint" style={{ color: 'var(--alert)' }}>
                  Die beiden stimmen nicht überein.
                </span>
              )}
            </div>
          </div>

          <div className="office-actions" style={{ marginTop: 18 }}>
            <button
              className="o-btn o-btn-primary"
              id="btn-passwort-aendern"
              disabled={busy !== null || !pwAlt || !pruefePasswort(pwNeu).ok || pwNeu !== pwNeu2}
              onClick={async () => {
                setPwFehler(null)
                setPwFertig(null)
                setBusy('passwort')
                try {
                  const r = await api.passwortAendern(pwAlt, pwNeu, uuidv7())
                  setPwAlt('')
                  setPwNeu('')
                  setPwNeu2('')
                  /*
                    Die Zahl der beendeten Sitzungen steht bewusst da. Sie ist
                    der Beweis, dass die Änderung gewirkt hat – und der Hinweis
                    an jemanden, der sein Passwort ändert, WEIL etwas nicht
                    stimmte: wer sonst noch angemeldet war, ist es jetzt nicht
                    mehr.
                  */
                  setPwFertig(
                    r.beendeteSitzungen > 0
                      ? `Passwort geändert. ${r.beendeteSitzungen} weitere Anmeldung${
                          r.beendeteSitzungen === 1 ? ' wurde' : 'en wurden'
                        } beendet – auf anderen Geräten ist eine neue Anmeldung nötig.`
                      : 'Passwort geändert. Diese Sitzung bleibt angemeldet.'
                  )
                  notify.ok('Passwort geändert.')
                } catch (err) {
                  setPwFehler(fehlerText(err))
                } finally {
                  setBusy(null)
                }
              }}
            >
              {busy === 'passwort' ? 'Ändert …' : 'Passwort ändern'}
            </button>
          </div>

          <p className="o-soft" style={{ marginTop: 14 }}>
            Mit dem neuen Passwort bleibt dieses Gerät angemeldet. Alle anderen Anmeldungen werden
            beendet – das ist der Sinn der Übung, wenn ein Passwort in falsche Hände geraten ist.
          </p>
        </div>
      </div>

      {/* ---------- Zustand ---------- */}

      <div className="o-card" id="sicherheit-status">
        <div className="o-card-head">
          <h3>Zwei-Faktor-Authentifizierung</h3>
          <span
            className={status.twoFactorEnabled ? 'o-badge o-badge-ok' : 'o-badge o-badge-alert'}
            id="zweifaktor-status"
          >
            {status.twoFactorEnabled ? 'aktiv' : 'nicht eingerichtet'}
          </span>
        </div>
        <div className="o-pad">
          {status.twoFactorEnabled ? (
            <>
              <p style={{ marginTop: 0 }}>
                Beim Anmelden verlangt Raporto zusätzlich zum Passwort einen sechsstelligen Code aus
                deiner Authenticator-App.
              </p>
              <p className="o-soft">
                Noch {status.wiederherstellungscodesOffen} unbenutzte Wiederherstellungscodes.
              </p>
              <div className="office-actions" style={{ marginTop: 12 }}>
                <button
                  className="o-btn"
                  id="btn-2fa-abschalten"
                  onClick={() => setSchritt('abschalten')}
                >
                  Abschalten
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={{ marginTop: 0 }}>
                {/*
                  Der Satz unterscheidet sich nach Rolle, weil die Folgen sich
                  unterscheiden. Beim Monteur ist nichts gesperrt – Zahlungen,
                  Exporte und der Rechnungsabschluss gehören ohnehin nicht zu
                  seinen Rechten. Ihm zu schreiben, sie seien gesperrt, wäre
                  eine Drohung mit etwas, das ihn nie betrifft.
                */}
                {!status.twoFactorRequired
                  ? 'Für dieses Konto ist derzeit kein zweiter Faktor verlangt. ' +
                    'Einrichten ist trotzdem empfohlen.'
                  : session.role === 'superuser'
                    ? 'Solange kein zweiter Faktor eingerichtet ist, sind Zahlungen, Exporte, ' +
                      'der Rechnungsabschluss, der Versand und die Firmendaten gesperrt. ' +
                      'Ansehen kannst du alles.'
                    : 'Solange kein zweiter Faktor eingerichtet ist, hängt dein Zugang allein ' +
                      'am Passwort. Am Erfassen und Unterschreiben von Rapporten ändert die ' +
                      'Einrichtung nichts – sie kommt nur beim Anmelden dazu.'}
              </p>
              <p className="o-soft">
                Du brauchst eine Authenticator-App auf dem Telefon – Google Authenticator, Microsoft
                Authenticator, 1Password, Bitwarden, Authy oder die Passwörter-App von Apple. Keine
                SMS.
              </p>
              <div className="office-actions" style={{ marginTop: 12 }}>
                <button
                  className="o-btn o-btn-primary"
                  id="btn-2fa-einrichten"
                  disabled={busy !== null}
                  onClick={() => void mit('setup', einrichtenStarten)}
                >
                  {busy === 'setup' ? 'Einen Moment …' : 'Jetzt einrichten'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ---------- Schritt 1: QR-Code ---------- */}

      {schritt === 'qr' && qrSvg && (
        <div className="o-card" id="sicherheit-qr">
          <div className="o-card-head">
            <h3>Schritt 1 · Code scannen</h3>
            <span className="o-soft">Aktiv wird der Schutz erst nach der Bestätigung.</span>
          </div>
          <div className="o-pad">
            <div className="o-2fa">
              <div
                className="o-qr"
                id="qr-bild"
                // Der QR-Code kommt fertig gezeichnet vom Server – die
                // Oberfläche lädt dafür nichts aus dem Netz nach.
                dangerouslySetInnerHTML={{ __html: qrSvg }}
              />
              <div>
                <p style={{ marginTop: 0 }}>
                  Öffne deine Authenticator-App, wähle „Konto hinzufügen“ und scanne diesen Code.
                </p>
                <div className="o-field">
                  <label htmlFor="schluessel">Ohne Kamera: Schlüssel abtippen</label>
                  <input
                    autoComplete="off"
                    id="schluessel"
                    className="o-mono"
                    readOnly
                    value={schluessel}
                  />
                </div>
                <div className="o-field" style={{ marginTop: 16 }}>
                  <label htmlFor="ex-2fa-code">Schritt 2 · Die sechs Ziffern aus der App</label>
                  <input
                    id="ex-2fa-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <div className="office-actions" style={{ marginTop: 12 }}>
                  <button
                    className="o-btn o-btn-primary"
                    id="btn-2fa-bestaetigen"
                    disabled={busy !== null || code.length < 6}
                    onClick={() => void mit('confirm', bestaetigen)}
                  >
                    {busy === 'confirm' ? 'Prüft …' : 'Bestätigen und aktivieren'}
                  </button>
                  <button className="o-btn o-btn-ghost" onClick={() => setSchritt('uebersicht')}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Schritt 3: Wiederherstellungscodes ---------- */}

      {schritt === 'codes' && (
        <div className="o-card" id="sicherheit-codes">
          <div className="o-card-head">
            <h3>Schritt 3 · Wiederherstellungscodes</h3>
            <span className="o-badge o-badge-warn">nur jetzt sichtbar</span>
          </div>
          <div className="o-pad">
            <p style={{ marginTop: 0 }}>
              Wenn das Telefon verloren geht, kommst du mit einem dieser Codes trotzdem hinein.
              Jeder funktioniert genau einmal. Bewahre sie ausserhalb des Telefons auf – ausgedruckt
              oder im Passwortmanager.
            </p>
            <div className="o-codes" id="recovery-codes">
              {codes.map((c) => (
                <span key={c} className="o-mono">
                  {c}
                </span>
              ))}
            </div>
            <label className="o-check" style={{ marginTop: 16 }}>
              <input
                autoComplete="off"
                type="checkbox"
                id="codes-gespeichert"
                checked={bestaetigt}
                onChange={(e) => setBestaetigt(e.target.checked)}
              />
              <span>Ich habe die Wiederherstellungscodes gespeichert.</span>
            </label>
            <div className="office-actions" style={{ marginTop: 12 }}>
              <button
                className="o-btn o-btn-primary"
                id="btn-codes-fertig"
                disabled={!bestaetigt}
                onClick={() => {
                  setCodes([])
                  setSchritt('uebersicht')
                  notify.ok('Zwei-Faktor-Authentifizierung ist aktiv.')
                }}
              >
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Abschalten ---------- */}

      {schritt === 'abschalten' && (
        <div className="o-card" id="sicherheit-abschalten">
          <div className="o-card-head">
            <h3>Zwei-Faktor-Authentifizierung abschalten</h3>
          </div>
          <div className="o-pad">
            <p style={{ marginTop: 0 }}>
              Zum Abschalten brauchst du beides: dein Passwort und einen gültigen Code. Eine offene
              Sitzung allein reicht nicht – sonst genügte ein unbeaufsichtigter Laptop.
            </p>
            <div className="o-grid o-grid-2">
              <div className="o-field">
                <label htmlFor="ab-passwort">Passwort</label>
                <input
                  id="ab-passwort"
                  type="password"
                  autoComplete="current-password"
                  value={passwort}
                  onChange={(e) => setPasswort(e.target.value)}
                />
              </div>
              <div className="o-field">
                <label htmlFor="ab-code">Code aus der App</label>
                <input
                  autoComplete="off"
                  id="ab-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={abschaltCode}
                  onChange={(e) => setAbschaltCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>
            <div className="office-actions" style={{ marginTop: 12 }}>
              <button
                className="o-btn"
                id="btn-abschalten-bestaetigen"
                disabled={busy !== null || !passwort || abschaltCode.length < 6}
                onClick={() => void mit('disable', abschalten)}
              >
                {busy === 'disable' ? 'Prüft …' : 'Endgültig abschalten'}
              </button>
              <button className="o-btn o-btn-ghost" onClick={() => setSchritt('uebersicht')}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
